import type { PropsWithChildren } from 'react';
import { useId } from 'react';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { getRarityColor } from '@sorare/core/src/lib/colors';

type Props = {
  rarity?: Rarity;
  className?: string;
};

export const CardPlaceholder = ({
  rarity,
  className,
  children,
}: PropsWithChildren<Props>) => {
  const cardPlaceholderId = useId();
  const color = rarity ? getRarityColor(rarity) : 'var(--c-nd-400)';
  return (
    <svg
      width="137"
      height="220"
      viewBox="0 0 137 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <g clipPath={`url(#card_border_clip_path_${cardPlaceholderId})`}>
        <path
          d="M0 8.00001C0 3.58173 3.58172 0 8 0H129C133.418 0 137 3.58172 137 8V212C137 216.418 133.418 220 129 220H8C3.58172 220 0 216.418 0 212V8.00001Z"
          fill="#101010"
        />
        <path
          d="M0 8.00001C0 3.58173 3.58172 0 8 0H129C133.418 0 137 3.58172 137 8V212C137 216.418 133.418 220 129 220H8C3.58172 220 0 216.418 0 212V8.00001Z"
          fill={`url(#background_gradient_${cardPlaceholderId})`}
        />
        <path
          d="M0 8.00001C0 3.58173 3.58172 0 8 0H129C133.418 0 137 3.58172 137 8V212C137 216.418 133.418 220 129 220H8C3.58172 220 0 216.418 0 212V8.00001Z"
          fill={`url(#dot_pattern_${cardPlaceholderId})`}
          fillOpacity="0.2"
          style={{ mixBlendMode: 'luminosity' }}
        />
        <g style={{ mixBlendMode: 'plus-lighter' }}>
          <g filter={`url(#bottom_shadow_${cardPlaceholderId})`}>
            <ellipse
              cx="68.709"
              cy="219.495"
              rx="59.287"
              ry="8.63793"
              transform="rotate(-180 68.709 219.495)"
              fill={color}
            />
          </g>
          <g
            opacity="0.2"
            filter={`url(#bottom_shadow_2_${cardPlaceholderId})`}
          >
            <ellipse
              cx="68.7091"
              cy="219.501"
              rx="59.287"
              ry="83.5"
              transform="rotate(-180 68.7091 219.501)"
              fill={color}
            />
          </g>
        </g>
        <g
          opacity="0.6"
          filter={`url(#player_silhouette_${cardPlaceholderId})`}
        >
          <path
            d="M132.586 138.279C132.329 137.701 132.256 135.463 132.256 134.778C132.256 134.092 132.44 132.466 132.147 130.624C131.853 128.783 131.121 117.662 130.422 112.606C129.726 107.552 127.599 97.6925 124.921 93.1068C123.417 90.2908 122.061 86.9327 122.061 86.9327C122.061 86.9327 114.288 79.9632 102.553 76.3903C94.5946 73.8275 92.2128 72.5641 91.4778 71.697C90.7453 70.83 90.1947 69.7482 90.1947 69.7482L83.3013 68.8095C83.3013 68.8095 82.8624 59.927 83.3013 57.3642C83.7403 54.8015 85.0987 50.1416 85.0987 50.1416C85.0987 50.1416 85.4285 50.9728 86.4181 50.9728C87.4077 50.9728 89.3895 49.2029 90.0492 47.3973C90.7089 45.5916 91.1115 44.4356 91.6622 43.6427C92.2128 42.8473 92.6881 40.4662 91.1842 39.3101C89.6804 38.1541 87.309 39.1669 87.309 39.1669C87.309 39.1669 87.5298 38.1311 87.9688 36.663C88.4077 35.1949 88.6544 30.5478 87.7739 27.1538C86.8934 23.7599 83.2052 11 69.003 11C54.8007 11 51.1099 23.7573 50.2294 27.1513C49.3489 30.5452 49.5931 35.1924 50.0346 36.6604C50.4736 38.1285 50.6944 39.1643 50.6944 39.1643C50.6944 39.1643 48.323 38.1541 46.8191 39.3076C45.3153 40.4636 45.7932 42.8473 46.3412 43.6401C46.8918 44.4356 47.2944 45.589 47.9542 47.3947C48.6139 49.2004 50.5931 50.9702 51.5852 50.9702C52.5774 50.9702 52.9047 50.139 52.9047 50.139C52.9047 50.139 54.2605 54.7964 54.702 57.3617C55.141 59.9244 54.702 68.807 54.702 68.807L47.8087 69.7456C47.8087 69.7456 47.2581 70.83 46.5256 71.6945C45.7932 72.5615 43.4088 73.825 35.4506 76.3877C23.7158 79.9632 15.942 86.9301 15.942 86.9301C15.942 86.9301 14.5861 90.2883 13.0823 93.1042C10.4044 97.69 8.27981 107.547 7.58113 112.603C6.88504 117.657 6.14999 128.78 5.85649 130.622C5.56299 132.463 5.74741 134.087 5.74741 134.775C5.74741 135.463 5.67468 137.701 5.41754 138.277C5.16041 138.855 4.93963 140.55 5.01496 141.202C5.08768 141.852 5.60195 142.43 5.60195 142.43C5.60195 142.43 5.96818 151.891 6.81231 156.548C7.65645 161.205 10.5525 163.625 15.1731 170.195C19.7938 176.766 25.9547 183.989 25.9547 183.989C25.9547 183.989 25.5521 189.26 25.7703 191.751C25.9911 194.242 26.43 194.35 25.0742 197.564C23.7184 200.777 23.7184 205.797 23.3885 209.588C23.0587 213.378 21.212 220.25 20.4302 221.672C19.6484 223.094 19.599 225.115 19.599 225.115C19.1341 225.742 19.1341 226.511 19.1341 226.511C19.1341 226.511 18.2796 227.57 17.9601 229.353C17.6432 231.133 19.012 231.279 19.012 231.279C21.5548 231.182 33.9493 232.772 40.6452 233.373C47.3438 233.974 57.4396 233.903 61.4473 233.903C65.455 233.903 69.0004 234 69.0004 234C69.0004 234 72.5458 233.903 76.5535 233.903C80.5611 233.903 90.6596 233.974 97.3555 233.373C104.054 232.772 116.449 231.184 118.989 231.279C118.989 231.279 120.358 231.135 120.041 229.353C119.724 227.573 118.867 226.511 118.867 226.511C118.867 226.511 118.867 225.742 118.402 225.115C118.402 225.115 118.352 223.092 117.571 221.672C116.789 220.253 114.942 213.381 114.612 209.588C114.282 205.797 114.282 200.777 112.927 197.564C111.571 194.352 112.01 194.242 112.23 191.751C112.451 189.26 112.046 183.989 112.046 183.989C112.046 183.989 118.207 176.766 122.828 170.195C127.448 163.625 130.344 161.205 131.188 156.548C132.033 151.891 132.399 142.43 132.399 142.43C132.399 142.43 132.913 141.852 132.986 141.202C133.059 140.553 132.84 138.855 132.583 138.277L132.586 138.279ZM30.6481 158.246C30.6481 158.246 25.7599 151.65 25.4171 149.965C25.0742 148.279 25.5885 146.668 25.5885 146.668C25.5885 146.668 27.178 146.236 27.4456 145.584C27.7157 144.934 28.5936 139.302 28.5936 139.302C28.5936 139.302 29.0325 138.627 29.1806 138.052C29.326 137.474 30.6481 128.038 30.6481 128.038C30.6481 128.038 30.0117 137.837 29.939 140.412C29.8663 142.988 29.9156 149.006 30.378 152.279C30.8403 155.553 30.6481 158.249 30.6481 158.249V158.246ZM112.589 149.965C112.246 151.65 107.358 158.246 107.358 158.246C107.358 158.246 107.163 155.551 107.628 152.277C108.093 149.003 108.142 142.985 108.067 140.41C107.992 137.834 107.358 128.036 107.358 128.036C107.358 128.036 108.677 137.471 108.825 138.049C108.971 138.627 109.412 139.3 109.412 139.3C109.412 139.3 110.293 144.931 110.56 145.581C110.831 146.231 112.417 146.666 112.417 146.666C112.417 146.666 112.932 148.279 112.589 149.962V149.965Z"
            fill={`url(#silhouette_gradient_${cardPlaceholderId})`}
          />
        </g>
      </g>
      <path
        d="M8 0.5H129C133.142 0.5 136.5 3.85786 136.5 8V212C136.5 216.142 133.142 219.5 129 219.5H8C3.85786 219.5 0.5 216.142 0.5 212V8L0.509766 7.61426C0.710539 3.65139 3.98724 0.5 8 0.5Z"
        stroke="white"
        strokeOpacity="0.17"
      />
      <path
        d="M8 0.5H129C133.142 0.5 136.5 3.85786 136.5 8V212C136.5 216.142 133.142 219.5 129 219.5H8C3.85786 219.5 0.5 216.142 0.5 212V8L0.509766 7.61426C0.710539 3.65139 3.98724 0.5 8 0.5Z"
        stroke={`url(#border_color_${cardPlaceholderId})`}
      />

      {children}

      <defs>
        <clipPath id={`card_border_clip_path_${cardPlaceholderId}`}>
          <path
            d="M8 0.5H129C133.142 0.5 136.5 3.85786 136.5 8V212C136.5 216.142 133.142 219.5 129 219.5H8C3.85786 219.5 0.5 216.142 0.5 212V8L0.509766 7.61426C0.710539 3.65139 3.98724 0.5 8 0.5Z"
            fill="white"
          />
        </clipPath>
        <filter
          id={`bottom_shadow_${cardPlaceholderId}`}
          x="-18.5781"
          y="182.857"
          width="174.574"
          height="73.2759"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="14"
            result="effect1_foregroundBlur_18595_54817"
          />
        </filter>
        <filter
          id={`bottom_shadow_2_${cardPlaceholderId}`}
          x="-26.5781"
          y="100.001"
          width="190.574"
          height="239"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="18"
            result="effect1_foregroundBlur_18595_54817"
          />
        </filter>
        <filter
          id={`player_silhouette_${cardPlaceholderId}`}
          x="3"
          y="9"
          width="132"
          height="227"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feGaussianBlur
            stdDeviation="1"
            result="effect1_foregroundBlur_18595_54817"
          />
        </filter>
        <linearGradient
          id={`background_gradient_${cardPlaceholderId}`}
          x1="110.55"
          y1="0"
          x2="110.55"
          y2="220"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopOpacity="0" />
          <stop
            stopColor="var(--card-pull-color)"
            offset="0.435822"
            stopOpacity="0.71"
          />
          <stop stopColor="var(--card-pull-color)" offset="1" />
        </linearGradient>
        <linearGradient
          id={`silhouette_gradient_${cardPlaceholderId}`}
          x1="67.829"
          y1="7.59831"
          x2="67.8295"
          y2="223.58"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#3F3F3F" />
          <stop offset="0.811652" stopColor="#3F3F3F" stopOpacity="0" />
        </linearGradient>
        <linearGradient
          id={`border_color_${cardPlaceholderId}`}
          x1="68.5"
          y1="0"
          x2="68.5"
          y2="220"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.5" stopOpacity="0" />
          <stop offset="1" stopColor={color} />
        </linearGradient>
        <clipPath id="card_border_clip_path">
          <path
            d="M0 8.00001C0 3.58173 3.58172 0 8 0H129C133.418 0 137 3.58172 137 8V212C137 216.418 133.418 220 129 220H8C3.58172 220 0 216.418 0 212V8.00001Z"
            fill="white"
          />
        </clipPath>
        <pattern
          id={`dot_pattern_${cardPlaceholderId}`}
          patternUnits="userSpaceOnUse"
          viewBox="-9513 650 48 48"
          width="2.8029196453790597%"
          height="1.7454545064405962%"
          patternContentUnits="objectBoundingBox"
        >
          <rect x="-9513" y="650" width="16" height="16" fill="#AC11FF" />
        </pattern>
      </defs>
    </svg>
  );
};
